import { Box as ChakraBox, Stack, Text } from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import {
  AlertCircle,
  Box as BoxIcon,
  CalendarCheck,
  Camera,
  Check,
  ClipboardList,
  CreditCard,
  MessageCircle,
  Image as ImageIcon,
  Package,
  PackageCheck,
  Play,
  Repeat2,
  ReceiptText,
  Star,
  Search,
  ShieldCheck,
  Truck,
  Upload,
  Video
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import Page from '../../components/Page';

const HEADER_TITLE_SCROLL_THRESHOLD = 48;
const SWIPE_DISTANCE_THRESHOLD = 56;
const SWIPE_VERTICAL_TOLERANCE = 1.2;

const guideTabs: { id: GuideTab; label: string }[] = [
  { id: 'sale', label: '판매' },
  { id: 'purchase', label: '구매' },
  { id: 'exchange', label: '교환' }
];

const saleSteps: GuideStep[] = [
  {
    number: 1,
    title: '준비 & 등록',
    badge: '등록',
    tone: 'orange',
    icon: Upload,
    bullets: [
      '슬리브, 탑로더/박스판, OPP 등 포장재 준비',
      '포카판에 사진, 가격, 배송 조건을 정확히 올리기',
      '보강재는 카드보다 사방 1cm 이상 여유 있게'
    ],
    visual: 'image-needed'
  },
  {
    number: 2,
    title: '연락 후 인증 & 고지',
    badge: '고지',
    tone: 'green',
    icon: Camera,
    bullets: [
      '구매자에게 연락이 오면 인증 사진/영상을 공유',
      '오늘 날짜 + 닉네임 + 포카명을 수기 메모로 인증',
      '빛 비춤 영상과 확대 사진으로 하자 정확히 공유'
    ],
    visual: 'image-needed'
  },
  {
    number: 3,
    title: '입금 & 배송 협의',
    badge: '협의',
    tone: 'blue',
    icon: CreditCard,
    bullets: [
      '준등기, 반값택배 등 배송 수단과 배송비 확인',
      '수령 정보와 최종 입금액을 구매자에게 안내',
      '입금 확인 후 약속한 배송 날짜 공유'
    ],
    visual: 'payment'
  },
  {
    number: 4,
    title: '포장 & 발송',
    badge: '발송',
    tone: 'red',
    icon: PackageCheck,
    bullets: [
      '슬리브 → 탑로더/박스판 → OPP/뽁뽁이 순서',
      '포장 단계별 사진을 남겨 구매자에게 공유',
      '운송장 번호를 바로 공유하고 영수증은 수령까지 보관'
    ],
    visual: 'image-needed'
  }
];

const exchangeSteps: GuideStep[] = [
  {
    number: 1,
    title: '등록 & 매칭',
    badge: '매칭',
    tone: 'orange',
    icon: Repeat2,
    bullets: [
      '교환하고 싶은 카드를 포카판에 올리기',
      '매칭 카드가 발견되면 상대에게 연락',
      '연락 후 인증과 상태 확인으로 넘어가기'
    ],
    visual: 'message'
  },
  {
    number: 2,
    title: '인증 & 상태 확인',
    badge: '확인',
    tone: 'green',
    icon: ShieldCheck,
    bullets: ['수기 메모 인증', '빛 비춤 영상 공유', '스크래치 / 찍힘 / 모서리 하자는 미리 고지'],
    visual: 'condition'
  },
  {
    number: 3,
    title: '발송 합의',
    badge: '협의',
    tone: 'blue',
    icon: Truck,
    bullets: ['동시 발송 권장', '선발송 시 서로 합의', '배송 수단과 일정 공유'],
    visual: 'shipping',
    chips: [
      { label: '준등기', checked: true, tone: 'gray' },
      { label: '반값택배', checked: true, tone: 'gray' },
      { label: '택배', checked: false, tone: 'gray' }
    ]
  },
  {
    number: 4,
    title: '수령 & 마무리',
    badge: '체크',
    tone: 'red',
    icon: BoxIcon,
    bullets: ['송장 공유', '무편집 개봉 영상 촬영', '이상 있으면 바로 전달'],
    visual: 'finish'
  }
];

const purchaseSteps: GuideStep[] = [
  {
    number: 1,
    title: '포카 선택 & 문의',
    badge: '문의',
    tone: 'orange',
    icon: MessageCircle,
    bullets: [
      '포카판에서 마음에 드는 포카를 선택',
      '판매글의 가격, 배송 조건, 상태를 먼저 확인',
      '구매 가능 여부를 명확하고 예의 있게 문의'
    ],
    visual: 'message'
  },
  {
    number: 2,
    title: '상태 & 인증 확인',
    badge: '확인',
    tone: 'green',
    icon: ShieldCheck,
    bullets: [
      '수기 메모 인증과 날짜, 닉네임 일치 여부 확인',
      '빛 비춤 영상에서 찍힘, 모서리 하자, 스크래치 확인',
      '상세 자료가 이미 있다면 중복 요청은 줄이기'
    ],
    visual: 'condition'
  },
  {
    number: 3,
    title: '입금 & 정보 전달',
    badge: '입금',
    tone: 'blue',
    icon: CreditCard,
    bullets: [
      '계좌를 받았다면 가급적 30분 안에 입금',
      '입금 후 확인 메시지와 확인증 캡처 공유',
      '성함, 연락처, 배송지, 우편번호를 한 번에 정확히 전달'
    ],
    visual: 'payment'
  },
  {
    number: 4,
    title: '수령 & 후기',
    badge: '수령',
    tone: 'red',
    icon: Star,
    bullets: [
      '택배를 뜯기 전 송장이 보이게 무편집 개봉 영상 촬영',
      '봉투 6면과 포장 해체, 카드 앞뒤 상태까지 끊김 없이 촬영',
      '잘 도착했다는 연락과 따뜻한 후기 한 줄 남기기'
    ],
    visual: 'unboxing'
  }
];

const saleQuickRules: QuickRule[] = [
  { icon: Upload, label: '포카판 등록', tone: 'orange' },
  { icon: AlertCircle, label: '하자 고지', tone: 'green' },
  { icon: PackageCheck, label: '안전 포장', tone: 'blue' },
  { icon: ReceiptText, label: '운송장 공유', tone: 'red' }
];

const exchangeQuickRules: QuickRule[] = [
  { icon: Repeat2, label: '자동 매칭', tone: 'orange' },
  { icon: ShieldCheck, label: '인증 확인', tone: 'green' },
  { icon: Truck, label: '동시 발송 권장', tone: 'blue' },
  { icon: Video, label: '개봉 영상 보관', tone: 'red' }
];

const purchaseQuickRules: QuickRule[] = [
  { icon: Search, label: '포카 선택', tone: 'orange' },
  { icon: ShieldCheck, label: '인증 확인', tone: 'green' },
  { icon: CreditCard, label: '빠른 입금', tone: 'blue' },
  { icon: Video, label: '개봉 영상', tone: 'red' }
];

const saleChecklist: ChecklistItem[] = [
  { icon: Upload, label: '포카판 판매글 등록', checked: true },
  { icon: Camera, label: '인증 사진 촬영', checked: true },
  { icon: AlertCircle, label: '하자 사진/영상 공유', checked: true },
  { icon: Truck, label: '배송비와 수령 정보 확인', checked: true },
  { icon: PackageCheck, label: '3단 포장 완료', checked: true },
  { icon: ReceiptText, label: '운송장 번호 공유', checked: true }
];

const exchangeChecklist: ChecklistItem[] = [
  { icon: Repeat2, label: '교환 카드 등록/매칭 확인', checked: true },
  { icon: ShieldCheck, label: '인증 사진 확인', checked: true },
  { icon: AlertCircle, label: '하자 고지 확인', checked: true },
  { icon: Package, label: '포장 방식 확인', checked: true },
  { icon: Truck, label: '송장 수령', checked: true },
  { icon: Video, label: '개봉 영상 촬영', checked: true }
];

const purchaseChecklist: ChecklistItem[] = [
  { icon: Search, label: '관심 포카 선택 후 문의', checked: true },
  { icon: Search, label: '시세와 상태 확인', checked: true },
  { icon: ShieldCheck, label: '인증 사진/영상 확인', checked: true },
  { icon: CreditCard, label: '입금 후 확인증 공유', checked: true },
  { icon: Video, label: '개봉 영상 촬영 준비', checked: true }
];

const toneClassName = {
  blue: {
    bg: 'bg-[#2F6BFF] dark:bg-[#4A7DFF]',
    soft: 'bg-[#2F6BFF]/10 dark:bg-[#4A7DFF]/14',
    text: 'text-[#2F6BFF] dark:text-[#7DA2FF]',
    badge: 'bg-[#2F6BFF] dark:bg-[#4A7DFF] text-onPrimary'
  },
  green: {
    bg: 'bg-onSuccess-light dark:bg-onSuccess-dark',
    soft: 'bg-onSuccess-light/10 dark:bg-onSuccess-dark/10',
    text: 'text-onSuccess-light dark:text-onSuccess-dark',
    badge: 'bg-onSuccess-light dark:bg-onSuccess-dark text-onPrimary'
  },
  purple: {
    bg: 'bg-primary-light dark:bg-primary-dark',
    soft: 'bg-secondary-light dark:bg-secondary-dark',
    text: 'text-primary-light dark:text-primary-dark',
    badge: 'bg-primary-light dark:bg-primary-dark text-onPrimary'
  },
  orange: {
    bg: 'bg-[#FF8A00] dark:bg-[#FF9F2E]',
    soft: 'bg-[#FF8A00]/10 dark:bg-[#FF9F2E]/14',
    text: 'text-[#F17800] dark:text-[#FFB05C]',
    badge: 'bg-[#FF8A00] dark:bg-[#FF9F2E] text-onPrimary'
  },
  red: {
    bg: 'bg-warning',
    soft: 'bg-warningContainer',
    text: 'text-warning',
    badge: 'bg-warning text-onWarning'
  },
  gray: {
    bg: 'bg-surface-lightLv2 dark:bg-surface-darkLv2',
    soft: 'bg-surface-lightLv2 dark:bg-surface-darkLv2',
    text: 'text-text-main-lv4',
    badge: 'bg-surface-lightLv2 dark:bg-surface-darkLv2 text-text-main-lv4'
  }
} as const;

const guideThemeClassName: Record<
  GuideTab,
  {
    accentText: string;
    ruleTone: keyof typeof toneClassName;
    selectedTab: string;
    summaryCard: string;
  }
> = {
  sale: {
    accentText: 'text-[#2F6BFF] dark:text-[#7DA2FF]',
    ruleTone: 'blue',
    selectedTab:
      'bg-[#2F6BFF] text-onPrimary shadow-[0px_2px_10px_0px_rgba(47,107,255,0.28),0px_0px_4px_0px_rgba(47,107,255,0.35)] dark:bg-[#4A7DFF]',
    summaryCard:
      'border-[#2F6BFF]/20 bg-[#2F6BFF]/[0.06] dark:border-[#4A7DFF]/30 dark:bg-[#4A7DFF]/[0.12]'
  },
  purchase: {
    accentText: 'text-onSuccess-light dark:text-onSuccess-dark',
    ruleTone: 'green',
    selectedTab:
      'bg-onSuccess-light text-onPrimary shadow-[0px_2px_10px_0px_rgba(60,201,116,0.28),0px_0px_4px_0px_rgba(60,201,116,0.35)] dark:bg-onSuccess-dark',
    summaryCard:
      'border-onSuccess-light/20 bg-onSuccess-light/10 dark:border-onSuccess-dark/30 dark:bg-onSuccess-dark/10'
  },
  exchange: {
    accentText: 'text-primary-light dark:text-primary-dark',
    ruleTone: 'purple',
    selectedTab:
      'bg-primary-light text-onPrimary shadow-[0px_2px_10px_0px_rgba(92,61,250,0.30),0px_0px_4px_0px_rgba(92,61,250,0.40)] dark:bg-primary-dark',
    summaryCard:
      'border-primary-light/20 bg-secondary-light dark:border-primary-dark/30 dark:bg-secondary-dark'
  }
};

const guideContents: Record<GuideTab, GuideContent> = {
  sale: {
    summary: {
      line1: '판매는',
      highlight1: '정확한 고지',
      between: '와 ',
      highlight2: '안전한 포장',
      after: '이 핵심이에요',
      icon: PackageCheck
    },
    quickRules: saleQuickRules,
    sectionTitle: '판매 가이드',
    steps: saleSteps,
    checklistTitle: '판매자 자가 체크 리스트',
    checklist: saleChecklist,
    notice: '하자 미고지, 부실 포장, 운송장 지연 공유는 분쟁으로 이어질 수 있어요.'
  },
  purchase: {
    summary: {
      line1: '구매는',
      highlight1: '확인',
      between: '과 ',
      highlight2: '매너',
      after: '가 핵심이에요',
      icon: ShieldCheck
    },
    quickRules: purchaseQuickRules,
    sectionTitle: '구매 가이드',
    steps: purchaseSteps,
    checklistTitle: '구매자 자가 체크 리스트',
    checklist: purchaseChecklist,
    notice: '개인 간 거래 사고는 포카판이 책임지지 않으니, 외부 메신저/현금 거래 유도에 주의하세요.'
  },
  exchange: {
    summary: {
      line1: '교환은 서로가',
      highlight1: '판매자',
      between: '이자 ',
      highlight2: '구매자',
      after: '예요',
      icon: Repeat2
    },
    quickRules: exchangeQuickRules,
    sectionTitle: '교환 가이드',
    steps: exchangeSteps,
    checklistTitle: '교환 자가 체크 리스트',
    checklist: exchangeChecklist,
    notice: '외부 메신저 유도, 과도한 재촉, 인증 없는 거래는 주의하세요.'
  }
};

const PocaTradeGuidePage = () => {
  const pageTitle = t('PocaTradeGuidePage.title', { defaultValue: '포카 거래 가이드' });
  const pageRef = useRef<HTMLDivElement | null>(null);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const [activeTab, setActiveTab] = useState<GuideTab>('exchange');
  const [showHeaderTitle, setShowHeaderTitle] = useState(false);
  const activeGuide = guideContents[activeTab];
  const activeGuideTheme = guideThemeClassName[activeTab];
  const activeTabIndex = guideTabs.findIndex((tab) => tab.id === activeTab);
  const SummaryIcon = activeGuide.summary.icon;
  const handleHistoryBack = useHandleHistoryBack();

  const moveActiveTabByOffset = useCallback((offset: number) => {
    setActiveTab((currentTab) => {
      const currentIndex = guideTabs.findIndex((tab) => tab.id === currentTab);
      const nextIndex = Math.max(0, Math.min(guideTabs.length - 1, currentIndex + offset));

      return guideTabs[nextIndex].id;
    });
  }, []);

  const handlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    swipeStartRef.current = {
      x: event.clientX,
      y: event.clientY
    };
  }, []);

  const handlePointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const startPoint = swipeStartRef.current;

      swipeStartRef.current = null;

      if (!startPoint) return;

      const deltaX = event.clientX - startPoint.x;
      const deltaY = event.clientY - startPoint.y;
      const isHorizontalSwipe =
        Math.abs(deltaX) >= SWIPE_DISTANCE_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_VERTICAL_TOLERANCE;

      if (!isHorizontalSwipe) return;

      moveActiveTabByOffset(deltaX < 0 ? 1 : -1);
    },
    [moveActiveTabByOffset]
  );

  const handlePointerCancel = useCallback(() => {
    swipeStartRef.current = null;
  }, []);

  useEffect(() => {
    const container = pageRef.current;

    const updateHeaderTitleVisibility = () => {
      const containerScrollTop = container?.scrollTop || 0;
      const windowScrollTop =
        window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const scrollTop = Math.max(containerScrollTop, windowScrollTop);

      setShowHeaderTitle(scrollTop > HEADER_TITLE_SCROLL_THRESHOLD);
    };

    updateHeaderTitleVisibility();

    container?.addEventListener('scroll', updateHeaderTitleVisibility);
    window.addEventListener('scroll', updateHeaderTitleVisibility);

    return () => {
      container?.removeEventListener('scroll', updateHeaderTitleVisibility);
      window.removeEventListener('scroll', updateHeaderTitleVisibility);
    };
  }, []);

  return (
    // @ts-expect-error Page is a legacy JS component without typed props.
    <Page
      ref={pageRef}
      title={pageTitle}
      showTitle={showHeaderTitle}
      onHistoryBackClick={handleHistoryBack}
    >
      <ChakraBox
        display={'flex'}
        flexDirection={'column'}
        minHeight={'calc((var(--vh, 1vh) * 100) - (44px + env(safe-area-inset-top)) - 48px)'}
      >
        <Stack spacing={'24px'} flex={1}>
          <Stack spacing={'6px'} paddingTop={'12px'} paddingBottom={'12px'} alignItems={'center'}>
            <Text
              className="text-main-light-1 dark:text-main-dark-1"
              fontSize={'28px'}
              fontWeight={700}
              textAlign={'center'}
            >
              {pageTitle}
            </Text>
            <Text
              className="text-main-light-3 dark:text-main-dark-3"
              fontSize={'15px'}
              textAlign={'center'}
            >
              안전하고 기분 좋은 거래를 위한 필수 매너
            </Text>
          </Stack>

          <div
            className="mx-auto w-full max-w-[430px] pb-[34px]"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            <nav
              aria-label="거래 유형"
              className="relative grid h-[56px] grid-cols-3 items-center overflow-hidden rounded-full border border-separator2 bg-background-light p-[2px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated dark:shadow-[0_0_4px_0_rgba(0,0,0,0.17),_0_0_24px_0_rgba(0,0,0,0.47)]"
            >
              <span
                aria-hidden={true}
                className={`pointer-events-none absolute left-[2px] top-[2px] h-[50px] w-[calc((100%_-_4px)/3)] rounded-full transition-all duration-300 ease-out will-change-transform ${activeGuideTheme.selectedTab}`}
                style={{ transform: `translateX(${activeTabIndex * 100}%)` }}
              />
              {guideTabs.map((tab, index) => {
                const isSelected = tab.id === activeTab;
                const isNextToSelectedTab = guideTabs[index - 1]?.id === activeTab;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative z-10 flex h-[50px] items-center justify-center rounded-full text-[15px] font-semibold leading-[18px] transition-colors duration-200 ${
                      isSelected ? 'text-onPrimary' : 'text-text-main-lv3'
                    }`}
                  >
                    {index > 0 && !isSelected && !isNextToSelectedTab && (
                      <span className="absolute left-0 h-[24px] w-[1px] bg-separator2" />
                    )}
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <section
              className={`mt-[28px] overflow-hidden rounded-[18px] border p-[18px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:shadow-[0_0_4px_0_rgba(0,0,0,0.17),_0_0_24px_0_rgba(0,0,0,0.47)] ${activeGuideTheme.summaryCard}`}
            >
              <div className="flex items-start justify-between gap-[8px]">
                <div className="min-w-0">
                  <p className="whitespace-pre-line text-[20px] font-extrabold leading-[27px] text-text-main-lv1">
                    {activeGuide.summary.line1}
                    {'\n'}
                    <span className={activeGuideTheme.accentText}>
                      {activeGuide.summary.highlight1}
                    </span>
                    {activeGuide.summary.between}
                    <span className={activeGuideTheme.accentText}>
                      {activeGuide.summary.highlight2}
                    </span>
                    {activeGuide.summary.after}
                  </p>
                </div>
                <div className="flex h-[64px] w-[64px] shrink-0 items-center justify-center rounded-full bg-background-light shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated">
                  <SummaryIcon
                    size={37}
                    className={activeGuideTheme.accentText}
                    strokeWidth={2.2}
                  />
                </div>
              </div>
              {activeGuide.quickRules.length > 0 && (
                <div className="mt-[18px] grid grid-cols-2 gap-[8px] min-[390px]:grid-cols-4">
                  {activeGuide.quickRules.map((rule) => (
                    <RulePill key={rule.label} rule={rule} toneKey={activeGuideTheme.ruleTone} />
                  ))}
                </div>
              )}
            </section>

            <section className="mt-[30px]">
              <h2 className="text-[20px] font-extrabold leading-[24px] text-text-main-lv1">
                {activeGuide.sectionTitle}
              </h2>
              {activeGuide.steps.length > 0 ? (
                <div className="mt-[16px] flex flex-col gap-[12px]">
                  {activeGuide.steps.map((step) => (
                    <GuideCard key={`${activeTab}-${step.number}`} step={step} />
                  ))}
                </div>
              ) : (
                <section className="mt-[16px] rounded-[16px] border border-separator2 bg-background-light p-[18px] text-center shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated">
                  <p className="text-[14px] font-semibold leading-[20px] text-text-main-lv2">
                    구매 가이드 자료를 받으면 이곳에 같은 형식으로 추가할게요.
                  </p>
                </section>
              )}
            </section>

            <section className="mt-[18px] rounded-[16px] border border-separator2 bg-background-light p-[18px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated dark:shadow-[0_0_4px_0_rgba(0,0,0,0.17),_0_0_24px_0_rgba(0,0,0,0.47)]">
              <div className="flex items-center justify-center gap-[8px] text-center">
                <ClipboardList
                  size={24}
                  className={activeGuideTheme.accentText}
                  strokeWidth={2.2}
                />
                <h2 className="text-[18px] font-extrabold leading-[23px] text-text-main-lv1">
                  {activeGuide.checklistTitle}
                </h2>
              </div>
              <div className="mt-[16px]">
                {activeGuide.checklist.map((item, index) => (
                  <ChecklistRow
                    key={item.label}
                    item={item}
                    isLast={index === activeGuide.checklist.length - 1}
                    toneKey={activeGuideTheme.ruleTone}
                  />
                ))}
              </div>
            </section>

            <section className="mt-[22px] flex items-center gap-[14px] rounded-[16px] border border-warning/20 bg-warningContainer px-[18px] py-[16px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)]">
              <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full border-[3px] border-warning text-warning">
                <AlertCircle size={26} strokeWidth={2.3} />
              </div>
              <div className="min-w-0">
                <h2 className="text-[20px] font-extrabold leading-[24px] text-warning">주의사항</h2>
                <p className="mt-[5px] text-[13px] font-semibold leading-[18px] text-text-main-lv2">
                  {activeGuide.notice}
                </p>
              </div>
            </section>
          </div>
        </Stack>
      </ChakraBox>
    </Page>
  );
};

export default PocaTradeGuidePage;

type GuideTab = 'sale' | 'purchase' | 'exchange';

interface GuideContent {
  summary: {
    line1: string;
    highlight1: string;
    between: string;
    highlight2: string;
    after: string;
    icon: LucideIcon;
  };
  quickRules: QuickRule[];
  sectionTitle: string;
  steps: GuideStep[];
  checklistTitle: string;
  checklist: ChecklistItem[];
  notice: string;
}

interface QuickRule {
  icon: LucideIcon;
  label: string;
  tone: keyof typeof toneClassName;
}

interface ChecklistItem {
  icon: LucideIcon;
  label: string;
  checked: boolean;
}

interface GuideStep {
  number: number;
  title: string;
  badge: string;
  tone: keyof typeof toneClassName;
  icon: LucideIcon;
  bullets: string[];
  visual: GuideVisualType;
  chips?: {
    label: string;
    checked: boolean;
    tone: keyof typeof toneClassName;
  }[];
}

const RulePill = ({ rule, toneKey }: { rule: QuickRule; toneKey?: keyof typeof toneClassName }) => {
  const tone = toneClassName[toneKey ?? rule.tone];
  const Icon = rule.icon;

  return (
    <div
      className={`flex min-h-[41px] items-center justify-center gap-[4px] rounded-full px-[7px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] ${tone.soft}`}
    >
      <Icon size={15} className={`shrink-0 ${tone.text}`} strokeWidth={2.2} />
      <span className="whitespace-nowrap text-[11px] font-bold leading-[14px] text-text-main-lv2">
        {rule.label}
      </span>
    </div>
  );
};

const GuideCard = ({ step }: { step: GuideStep }) => {
  const tone = toneClassName[step.tone];
  const Icon = step.icon;

  return (
    <article className="rounded-[16px] border border-separator2 bg-background-light px-[11px] py-[18px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated dark:shadow-[0_0_4px_0_rgba(0,0,0,0.17),_0_0_24px_0_rgba(0,0,0,0.47)]">
      <div className="grid grid-cols-[1fr_118px] gap-[12px]">
        <div className="min-w-0">
          <div className="flex items-center gap-[7px] pl-[8px]">
            <span className="shrink-0 text-[20px] font-extrabold leading-[24px] text-text-main-lv1">
              {step.number}.
            </span>
            <h3 className="min-w-0 text-[20px] font-extrabold leading-[24px] text-text-main-lv1">
              {step.title}
            </h3>
            <span
              className={`inline-flex shrink-0 items-center gap-[4px] rounded-full px-[8px] py-[5px] text-[11px] font-extrabold leading-[14px] ${tone.badge}`}
            >
              <Icon size={13} className="shrink-0 text-onPrimary" strokeWidth={2.3} />
              {step.badge}
            </span>
          </div>
          <ul className="mt-[14px] flex flex-col gap-[8px] pl-[10px]">
            {step.bullets.map((bullet) => (
              <li
                key={bullet}
                className="flex gap-[8px] text-[14px] font-medium leading-[18px] text-text-main-lv2"
              >
                <span className="mt-[7px] h-[4px] w-[4px] shrink-0 rounded-full bg-current" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative flex min-h-[106px] items-end justify-end">
          <GuideVisual type={step.visual} />
        </div>
      </div>
      {step.chips && (
        <div className="mt-[14px] flex flex-wrap gap-[6px]">
          {step.chips.map((chip) => (
            <DeliveryChip key={chip.label} chip={chip} />
          ))}
        </div>
      )}
    </article>
  );
};

type GuideVisualType =
  | 'verify'
  | 'condition'
  | 'shipping'
  | 'finish'
  | 'image-needed'
  | 'payment'
  | 'message'
  | 'unboxing';

function GuideVisual({ type }: { type: GuideVisualType }) {
  if (type === 'verify') return <VerifyVisual />;
  if (type === 'condition') return <ConditionVisual />;
  if (type === 'shipping') return <ShippingVisual />;
  if (type === 'image-needed') return <ImageNeededVisual />;
  if (type === 'payment') return <PaymentVisual />;
  if (type === 'message') return <MessageVisual />;
  if (type === 'unboxing') return <UnboxingVisual />;
  return <FinishVisual />;
}

const DeliveryChip = ({ chip }: { chip: NonNullable<GuideStep['chips']>[number] }) => {
  return (
    <div className="inline-flex h-[26px] w-fit items-center justify-center gap-[4px] rounded-full bg-surface-lightLv2 px-[8px] dark:bg-surface-darkLv2">
      <span
        className={`flex h-[13px] w-[13px] items-center justify-center rounded-full ${
          chip.checked
            ? 'bg-text-main-lv4'
            : 'border border-disabled-light bg-background-light dark:border-disabled-dark dark:bg-background-elevated'
        }`}
      >
        {chip.checked && <Check size={8} className="text-onPrimary" strokeWidth={3.2} />}
      </span>
      <span className="whitespace-nowrap text-[11px] font-semibold leading-[14px] text-text-main-lv3">
        {chip.label}
      </span>
    </div>
  );
};

const ChecklistRow = ({
  item,
  isLast,
  toneKey
}: {
  item: ChecklistItem;
  isLast: boolean;
  toneKey: keyof typeof toneClassName;
}) => {
  const Icon = item.icon;
  const tone = toneClassName[toneKey];

  return (
    <div
      className={`flex h-[44px] items-center justify-between gap-[12px] ${
        isLast ? '' : 'border-b border-separator1'
      }`}
    >
      <div className="flex items-center gap-[12px] min-w-0">
        <span className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-surface-lightLv1 text-text-main-lv2 dark:bg-surface-darkLv1">
          <Icon size={18} strokeWidth={2.2} />
        </span>
        <span className="truncate text-[15px] font-medium leading-[18px] text-text-main-lv2">
          {item.label}
        </span>
      </div>
      <span
        className={`flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[4px] ${
          item.checked
            ? tone.bg
            : 'border-2 border-disabled-light bg-background-light dark:border-disabled-dark dark:bg-background-elevated'
        }`}
      >
        {item.checked && <Check size={17} className="text-onPrimary" strokeWidth={3} />}
      </span>
    </div>
  );
};

const ImageNeededVisual = () => (
  <div className="relative flex h-[91px] w-[116px] flex-col items-center justify-center rounded-[10px] border border-separator2 bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-background-light text-primary-light shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated dark:text-primary-dark">
      <ImageIcon size={20} strokeWidth={2.2} />
    </div>
    <span className="mt-[8px] text-[13px] font-extrabold leading-[16px] text-text-main-lv2">
      이미지
    </span>
    <span className="mt-[2px] text-[10px] font-semibold leading-[13px] text-text-main-lv4">
      예시 필요
    </span>
  </div>
);

const PaymentVisual = () => (
  <div className="relative h-[91px] w-[116px] rounded-[10px] bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="absolute left-[17px] top-[18px] h-[45px] w-[67px] rounded-[9px] bg-background-light shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated">
      <div className="mt-[10px] h-[6px] w-full bg-separator2" />
      <div className="ml-[10px] mt-[9px] h-[5px] w-[32px] rounded-full bg-separator1" />
      <div className="ml-[10px] mt-[5px] h-[5px] w-[22px] rounded-full bg-separator1" />
    </div>
    <div className="absolute bottom-[13px] right-[12px] flex h-[34px] w-[34px] items-center justify-center rounded-full bg-warning text-onWarning">
      <CreditCard size={18} strokeWidth={2.2} />
    </div>
  </div>
);

const MessageVisual = () => (
  <div className="relative h-[91px] w-[116px] rounded-[10px] bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="absolute left-[15px] top-[18px] h-[31px] w-[58px] rounded-[11px] rounded-bl-[4px] bg-background-light shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated">
      <div className="ml-[10px] mt-[9px] h-[5px] w-[35px] rounded-full bg-separator2" />
      <div className="ml-[10px] mt-[5px] h-[5px] w-[24px] rounded-full bg-separator1" />
    </div>
    <div className="absolute right-[12px] top-[39px] h-[34px] w-[65px] rounded-[12px] rounded-br-[4px] bg-onSuccess-light/80 shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-onSuccess-dark/80">
      <div className="ml-[12px] mt-[10px] h-[5px] w-[39px] rounded-full bg-onPrimary/80" />
      <div className="ml-[12px] mt-[6px] h-[5px] w-[27px] rounded-full bg-onPrimary/65" />
    </div>
  </div>
);

const UnboxingVisual = () => (
  <div className="relative h-[91px] w-[116px] rounded-[10px] bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="absolute left-[18px] top-[23px] h-[42px] w-[65px] rounded-[6px] bg-[#C8904F] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)]">
      <div className="absolute left-[28px] top-0 h-full w-[9px] bg-[#E8D2B5]" />
      <div className="absolute left-[10px] top-[14px] h-[8px] w-[22px] rounded-[2px] bg-[#F3E5D1]" />
    </div>
    <div className="absolute bottom-[10px] right-[5px] flex h-[43px] w-[55px] items-center justify-center rounded-[8px] bg-pure-black/45 shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)]">
      <Video size={24} className="text-white" strokeWidth={2.1} />
    </div>
  </div>
);

const VerifyVisual = () => (
  <div className="relative h-[91px] w-[116px] rounded-[10px] bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="absolute right-[10px] top-[12px] h-[64px] w-[48px] rounded-[8px] border border-separator2 bg-background-light shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated">
      <div className="mx-auto mt-[7px] h-[30px] w-[30px] rounded-full bg-[linear-gradient(135deg,#D6DAE5,#A8B2C6)]" />
      <div className="mx-auto mt-[5px] h-[4px] w-[30px] rounded-full bg-separator2" />
      <div className="mx-auto mt-[4px] h-[4px] w-[22px] rounded-full bg-separator1" />
    </div>
    <div className="absolute left-[18px] top-[20px] h-[63px] w-[49px] rotate-[-2deg] rounded-[5px] border border-separator2 bg-background-light p-[6px] text-center shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated">
      <div className="text-[7px] font-bold leading-[9px] text-text-main-lv3">2024.05.16</div>
      <div className="mt-[5px] text-[6px] font-semibold leading-[8px] text-text-main-lv4">
        닉네임
      </div>
      <div className="text-[6px] font-semibold leading-[8px] text-text-main-lv4">000 포카</div>
      <div className="mt-[5px] text-[8px] text-text-main-lv4">♡ ♡ ♡</div>
    </div>
  </div>
);

const ConditionVisual = () => (
  <div className="relative h-[91px] w-[116px] rounded-[10px] bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="absolute left-[30px] top-[14px] h-[68px] w-[55px] rotate-[-12deg] overflow-hidden rounded-[9px] bg-[linear-gradient(135deg,#BBC8D8,#F4F7FB)] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)]">
      <div className="mx-auto mt-[13px] h-[38px] w-[38px] rounded-full bg-[linear-gradient(135deg,#C8CDD7,#808CA0)]" />
      <div className="absolute left-[-4px] top-[11px] h-[10px] w-[80px] rotate-[23deg] bg-white/40 blur-[2px]" />
    </div>
    <span className="absolute right-[18px] top-[30px] text-[24px] leading-none text-[#1FC67D]">
      ✦
    </span>
    <span className="absolute right-[9px] top-[47px] text-[18px] leading-none text-[#1FC67D]">
      ✦
    </span>
  </div>
);

const ShippingVisual = () => (
  <div className="relative h-[91px] w-[116px] rounded-[10px] bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="absolute left-[14px] top-[16px] h-[35px] w-[55px] rounded-[8px] rounded-bl-[3px] bg-[#2F7AF0]" />
    <div className="absolute right-[12px] top-[35px] h-[34px] w-[57px] rounded-[8px] rounded-br-[3px] bg-[#27C27B]" />
    <div className="absolute bottom-[8px] left-[40px] h-[38px] w-[45px] rounded-[8px] bg-background-light shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)] dark:bg-background-elevated">
      <div className="h-[11px] rounded-t-[8px] bg-[#6E5FE2]" />
      <CalendarCheck
        size={29}
        className="absolute left-[8px] top-[11px] text-[#6E5FE2]"
        strokeWidth={2.1}
      />
    </div>
  </div>
);

const FinishVisual = () => (
  <div className="relative h-[91px] w-[116px] rounded-[10px] bg-surface-lightLv1 dark:bg-surface-darkLv1">
    <div className="absolute left-[18px] top-[22px] h-[45px] w-[68px] rounded-[6px] bg-[#C8904F] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)]">
      <div className="absolute left-[30px] top-0 h-full w-[10px] bg-[#E8D2B5]" />
      <div className="absolute left-[12px] top-[15px] h-[9px] w-[22px] rounded-[2px] bg-[#F3E5D1]" />
    </div>
    <div className="absolute bottom-[10px] right-[4px] flex h-[44px] w-[55px] items-center justify-center rounded-[8px] bg-pure-black/45 shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.07)]">
      <Play size={26} className="fill-white text-white" strokeWidth={1.6} />
    </div>
  </div>
);
